import { createFileRoute, Link, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import { useEffect, useMemo, useState } from 'react'
import { CustomerChrome } from '#bv/components/customer/CustomerChrome'
import { TrackedOrdersStore } from '#bv/components/customer/TrackedOrdersStore'
import { getBuyerOrderCredentials, getBuyerOrderStatus, type BuyerOrderCredentialsOk, type BuyerOrderStatusOk } from '#bv/lib/httpBuyerOrderClient'
import { BuyerSmmFulfillment } from '#bv/components/customer/BuyerSmmFulfillment'
import { OrderStatusPill } from '#bv/components/customer/OrderStatusPill'
import { DiscordIcon } from '#bv/components/DiscordIcon'
import { hasBuyerSmmOrder, smmAutoDeliveryHint } from '#bv/lib/buyerSmmStatus'
import { buildVouchPostTemplate, PulseDigital_VOUCHES_CHANNEL, PulseDigital_VOUCHES_CHANNEL_URL, VOUCH_POST_DEADLINE_HOURS } from '#bv/lib/replacementPolicy'

const searchSchema = z.object({ email: z.string().optional() })

export const Route = createFileRoute('/customer/orders/$orderId')({
  validateSearch: searchSchema,
  component: CustomerOrderDetailPage,
})

function ProgressSteps({ status, readyForDelivery }: { status: string; readyForDelivery: boolean }) {
  const st = (status || '').toLowerCase()
  const steps = [
    { label: 'Order placed', done: true },
    { label: 'Payment received', done: st === 'paid' || readyForDelivery },
    { label: 'Ready for delivery', done: readyForDelivery },
  ]
  const cancelled = st === 'cancelled' || st === 'refunded'

  if (cancelled) {
    return (
      <div className="flex items-center gap-3 rounded-xl border border-white/[0.06] px-4 py-3" style={{ background: 'rgba(0,0,0,0.2)' }}>
        <div className="flex h-8 w-8 items-center justify-center rounded-full" style={{ background: st === 'cancelled' ? 'rgba(239,68,68,0.12)' : 'rgba(56,189,248,0.12)' }}>
          {st === 'cancelled'
            ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#f87171" strokeWidth="2"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
            : <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#38bdf8" strokeWidth="2"><polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 2.13-9.36L1 10"/></svg>
          }
        </div>
        <span className="text-sm font-semibold" style={{ color: st === 'cancelled' ? '#f87171' : '#38bdf8' }}>
          Order {st === 'cancelled' ? 'cancelled' : 'refunded'}
        </span>
      </div>
    )
  }

  return (
    <div className="flex items-center gap-0" role="progressbar">
      {steps.map((step, i) => (
        <div key={step.label} className="flex items-center" style={{ flex: i < steps.length - 1 ? 1 : undefined }}>
          <div className="flex flex-col items-center">
            <div className="flex h-8 w-8 items-center justify-center rounded-full text-xs font-bold" style={step.done ? { background: 'rgba(16,185,129,0.15)', color: '#34d399', border: '1.5px solid rgba(16,185,129,0.3)' } : { background: 'rgba(255,255,255,0.03)', color: '#5a6d8a', border: '1.5px solid rgba(255,255,255,0.06)' }}>
              {step.done
                ? <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>
                : i + 1}
            </div>
            <span className="mt-1.5 text-[0.6rem] font-semibold" style={{ color: step.done ? '#34d399' : '#5a6d8a' }}>{step.label}</span>
          </div>
          {i < steps.length - 1 ? (
            <div className="mx-2 h-0.5 flex-1 rounded-full" style={{ background: steps[i + 1].done ? 'rgba(16,185,129,0.3)' : 'rgba(255,255,255,0.06)', marginBottom: '18px' }} />
          ) : null}
        </div>
      ))}
    </div>
  )
}

function CustomerOrderDetailPage() {
  const navigate = useNavigate()
  const { orderId } = Route.useParams()
  const search = Route.useSearch()

  const [email, setEmail] = useState(search.email?.trim() ?? '')
  const [copied, setCopied] = useState<'template' | 'id' | 'vouch' | ''>('')

  const [statusRow, setStatusRow] = useState<
    | { loading: true }
    | { loading: false; ok: true; data: BuyerOrderStatusOk }
    | { loading: false; ok: false; error: string; status?: number }
  >({ loading: true })
  const [credRow, setCredRow] = useState<
    | { loading: false; ok: false; error: string; status?: number }
    | { loading: true }
    | { loading: false; ok: true; data: BuyerOrderCredentialsOk }
  >({ loading: false, ok: false, error: '' })

  const storedEmail = useMemo(() => { const hit = TrackedOrdersStore.getOne(orderId); return hit?.email ?? '' }, [orderId])

  useEffect(() => { if (!email && storedEmail) setEmail(storedEmail) }, [email, storedEmail])

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      setStatusRow({ loading: true })
      try {
        const res = await getBuyerOrderStatus({ orderId, email: email.trim() || undefined })
        if (cancelled) return
        if (!res.ok) { setStatusRow({ loading: false, ok: false, error: res.error, status: res.status }); setCredRow({ loading: false, ok: false, error: '' }); return }
        setStatusRow({ loading: false, ok: true, data: res.data })
      } catch (e) {
        if (cancelled) return
        setStatusRow({ loading: false, ok: false, error: e instanceof Error ? e.message : 'Request failed' })
        setCredRow({ loading: false, ok: false, error: '' })
      }
    }
    void run()
    const id = window.setInterval(() => void run(), 5000)
    return () => { cancelled = true; window.clearInterval(id) }
  }, [orderId, email])

  useEffect(() => {
    if (!statusRow.loading && statusRow.ok && statusRow.data.readyForDelivery !== true) setCredRow({ loading: false, ok: false, error: '' })
  }, [statusRow])

  const fetchCredentials = async () => {
    setCredRow({ loading: true })
    try {
      const res = await getBuyerOrderCredentials({ orderId, email: email.trim() || undefined })
      if (!res.ok) { setCredRow({ loading: false, ok: false, error: res.error, status: res.status }); return }
      setCredRow({ loading: false, ok: true, data: res.data })
    } catch (e) {
      setCredRow({ loading: false, ok: false, error: e instanceof Error ? e.message : 'Request failed' })
    }
  }

  const copyText = async (value: string, kind: 'template' | 'id' | 'vouch') => {
    try { await navigator.clipboard.writeText(value); setCopied(kind); window.setTimeout(() => setCopied(''), 1800) } catch {}
  }

  const vouchTemplate = useMemo(() => {
    const product = statusRow.ok ? statusRow.data.productName?.trim() || '' : ''
    return buildVouchPostTemplate(orderId, product)
  }, [orderId, statusRow])

  const saveToBrowser = () => {
    const em = email.trim()
    if (!em) return
    TrackedOrdersStore.add({ orderId, email: em })
    void navigate({ to: '/customer/orders/$orderId', params: { orderId }, search: { email: em }, replace: true })
  }

  return (
    <CustomerChrome active="orders" title="Order details" subtitle="Verify your status and access delivery instructions.">
      {/* Breadcrumb */}
      <div className="mb-4 flex items-center gap-2 text-xs text-[#5a6d8a]">
        <Link to="/customer/orders" className="hover:text-[#8ecfff] transition-colors">Orders</Link>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M9 18l6-6-6-6"/></svg>
        <span className="font-mono text-[#5a6d8a] truncate max-w-[200px]">{orderId}</span>
      </div>

      {/* Main card */}
      <div className="rounded-2xl border border-white/[0.06] overflow-hidden" style={{ background: 'rgba(10,8,16,0.8)' }}>
        {/* Order header */}
        <div className="border-b border-white/[0.06] px-5 py-4" style={{ background: 'rgba(0,0,0,0.2)' }}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <p className="m-0 text-[0.6rem] font-bold uppercase tracking-widest text-[#5a6d8a]">Order ID</p>
              <p className="m-0 mt-1 font-mono text-sm text-[#8ecfff] break-all">{orderId}</p>
            </div>
            <div className="flex items-center gap-2">
              {statusRow.loading ? (
                <span className="rounded-lg border border-white/[0.06] bg-white/[0.02] px-3 py-1.5 text-xs text-[#5a6d8a]">Loading...</span>
              ) : statusRow.ok ? (
                <OrderStatusPill status={statusRow.data.status} readyForDelivery={statusRow.data.readyForDelivery} />
              ) : (
                <span className="rounded-lg border border-red-500/25 bg-red-500/10 px-3 py-1.5 text-xs text-red-200">Error</span>
              )}
            </div>
          </div>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" className="inline-flex items-center gap-1.5 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3.5 py-2 text-xs font-semibold text-[#7a92b8] transition-colors hover:text-[#f0f6ff] hover:border-[rgba(0, 191, 255,0.15)]" onClick={() => void copyText(orderId, 'id')}>
              {copied === 'id' ? (
                <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>Copied</>
              ) : (
                <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>Copy ID</>
              )}
            </button>
            <Link to="/customer/orders" className="inline-flex items-center gap-1.5 rounded-xl border border-white/[0.06] bg-white/[0.02] px-3.5 py-2 text-xs font-semibold text-[#7a92b8] transition-colors hover:text-[#f0f6ff]">
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M19 12H5M12 19l-7-7 7-7"/></svg>All orders
            </Link>
          </div>
        </div>

        <div className="p-5 sm:p-6 space-y-5">
          {/* Email + save */}
          <div className="rounded-xl border border-white/[0.06] p-4" style={{ background: 'rgba(0,0,0,0.15)' }}>
            <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_auto]">
              <label className="block">
                <span className="mb-1 block text-[0.6rem] font-bold uppercase tracking-widest text-[#5a6d8a]">Checkout email</span>
                <input className="bv-input w-full rounded-xl px-4 py-3 text-sm" placeholder="Same email used at checkout" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email" />
                <span className="mt-1 block text-[0.6rem] leading-snug text-[#5a6d8a]">Some orders require email verification. If yours doesn't, leave this blank.</span>
              </label>
              <div className="flex items-end">
                <button type="button" className="btn-primary rounded-xl px-5 py-3 text-sm font-semibold" onClick={saveToBrowser} disabled={!email.trim()} title="Save this order to your browser">
                  Save
                </button>
              </div>
            </div>
          </div>

          {/* Error banner */}
          {!statusRow.loading && !statusRow.ok ? (
            <div className="flex items-start gap-3 rounded-xl border border-amber-500/15 px-4 py-3" style={{ background: 'rgba(245,158,11,0.04)' }}>
              <svg className="mt-0.5 shrink-0" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#fbbf24" strokeWidth="2"><path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
              <p className="m-0 text-xs text-amber-100">
                {statusRow.error === 'email_required' ? 'This order needs email verification. Enter the checkout email and try again.'
                  : statusRow.error === 'unauthorized' ? 'Email does not match this order.'
                  : statusRow.error === 'not_found' ? 'Order not found yet (webhook may still be processing).'
                  : statusRow.error}
              </p>
            </div>
          ) : null}

          {/* Progress + summary */}
          {!statusRow.loading && statusRow.ok ? (
            <>
              <ProgressSteps status={statusRow.data.status} readyForDelivery={statusRow.data.readyForDelivery} />

              {hasBuyerSmmOrder(statusRow.data) ? (
                <BuyerSmmFulfillment
                  {...statusRow.data}
                  orderId={orderId}
                  buyerEmail={email.trim() || undefined}
                />
              ) : null}

              <div className="grid gap-4 lg:grid-cols-2">
                {/* Summary */}
                <div className="rounded-xl border border-white/[0.06] p-4" style={{ background: 'rgba(0,0,0,0.15)' }}>
                  <p className="m-0 text-[0.6rem] font-bold uppercase tracking-widest text-[#5a6d8a]">Summary</p>
                  <p className="m-0 mt-2.5 text-sm font-semibold text-[#f0f6ff]">{statusRow.data.productName ?? '—'}</p>
                  <div className="mt-2 space-y-1.5">
                    <div className="flex items-center gap-2 text-xs">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5a6d8a" strokeWidth="2"><path d="M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                      <span className="text-[#5a6d8a]">{statusRow.data.amountUsd != null ? `$${Number(statusRow.data.amountUsd).toFixed(2)}` : '—'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5a6d8a" strokeWidth="2"><rect x="1" y="4" width="22" height="16" rx="2"/><line x1="1" y1="10" x2="23" y2="10"/></svg>
                      <span className="text-[#5a6d8a]">{statusRow.data.channel === 'crypto' ? 'Crypto' : statusRow.data.channel === 'paypal_ff' ? 'PayPal F&F' : 'Card'}</span>
                    </div>
                    <div className="flex items-center gap-2 text-xs">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#5a6d8a" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      <span className="text-[#5a6d8a]">{statusRow.data.createdAt ? new Date(statusRow.data.createdAt).toLocaleString() : '—'}</span>
                    </div>
                  </div>
                </div>

                {/* Delivery */}
                <div className="rounded-xl border border-white/[0.06] p-4" style={{ background: 'rgba(0,0,0,0.15)' }}>
                  <p className="m-0 text-[0.6rem] font-bold uppercase tracking-widest text-[#5a6d8a]">Delivery</p>
                  {statusRow.data.readyForDelivery ? (
                    <div className="mt-2.5 flex items-start gap-2">
                      <svg className="mt-0.5 shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
                      <p className="m-0 text-sm text-emerald-200">Payment verified — open Discord and paste the delivery template.</p>
                    </div>
                  ) : (
                    <div className="mt-2.5 flex items-start gap-2">
                      <svg className="mt-0.5 shrink-0" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#5a6d8a" strokeWidth="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      <p className="m-0 text-sm text-[#5a6d8a]">Not ready yet — auto-refreshes every few seconds.</p>
                    </div>
                  )}
                  {!hasBuyerSmmOrder(statusRow.data) ? (
                    <>
                      <button type="button" className="btn-primary mt-4 w-full rounded-xl px-5 py-3 text-sm font-semibold disabled:opacity-50" onClick={() => void fetchCredentials()} disabled={!statusRow.data.readyForDelivery}>
                        {credRow.loading ? 'Loading...' : 'Get delivery instructions'}
                      </button>
                      {!credRow.loading && !credRow.ok && credRow.error ? (
                        <p className="m-0 mt-3 text-xs text-amber-200">
                          {credRow.error === 'not_ready' ? 'Not ready yet — try again in a moment.'
                            : credRow.error === 'email_required' ? 'Enter the checkout email and try again.'
                            : credRow.error === 'unauthorized' ? 'Email does not match this order.'
                            : credRow.error}
                        </p>
                      ) : null}
                    </>
                  ) : (
                    <p className="m-0 mt-3 text-xs text-[#5a6d8a]">
                      {statusRow.data.readyForDelivery
                        ? smmAutoDeliveryHint(statusRow.data.smmPlatform, statusRow.data.smmMetric)
                        : 'Waiting for payment confirmation.'}
                    </p>
                  )}
                </div>
              </div>
            </>
          ) : null}
        </div>
      </div>

      {/* Delivery instructions (expanded) */}
      {statusRow.ok && hasBuyerSmmOrder(statusRow.data) ? null : credRow.loading ? (
        <div className="mt-5 rounded-2xl border border-white/[0.06] p-5 sm:p-6" style={{ background: 'rgba(10,8,16,0.8)' }}>
          <div className="flex items-center gap-3">
            <div className="h-5 w-5 rounded-full border-2 border-[#00bfff] border-t-transparent" style={{ animation: 'spin 0.8s linear infinite' }} />
            <p className="m-0 text-sm text-[#5a6d8a]">Loading delivery instructions...</p>
          </div>
        </div>
      ) : !credRow.loading && credRow.ok ? (
        <div className="mt-5 rounded-2xl border overflow-hidden" style={{ borderColor: 'rgba(16,185,129,0.15)', background: 'rgba(10,8,16,0.8)' }}>
          <div className="border-b px-5 py-4" style={{ borderColor: 'rgba(16,185,129,0.1)', background: 'rgba(16,185,129,0.03)' }}>
            <div className="flex items-center gap-3">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: 'rgba(16,185,129,0.1)' }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="2"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <div>
                <h2 className="m-0 text-sm font-extrabold tracking-tight text-[#f0f6ff]">Delivery instructions</h2>
                <p className="m-0 text-[0.6rem] text-emerald-200/60">{credRow.data.delivery.instructions}</p>
              </div>
            </div>
          </div>
          <div className="p-5 sm:p-6">
            <p className="m-0 text-[0.6rem] font-bold uppercase tracking-widest text-[#5a6d8a]">Discord ticket template</p>
            <pre className="mt-3 max-h-72 overflow-auto whitespace-pre-wrap break-words rounded-xl border border-white/[0.06] p-4 font-mono text-xs leading-relaxed text-[#e8ecff]" style={{ background: 'rgba(0,0,0,0.2)' }}>
              {credRow.data.delivery.discordMessageTemplate}
            </pre>
            <div className="mt-3 flex flex-wrap gap-2">
              <button type="button" onClick={() => void copyText(credRow.data.delivery.discordMessageTemplate, 'template')} className="inline-flex items-center gap-1.5 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-2.5 text-xs font-semibold text-[#7a92b8] transition-colors hover:text-[#f0f6ff] hover:border-[rgba(0, 191, 255,0.15)]">
                {copied === 'template' ? (
                  <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="3"><polyline points="20 6 9 17 4 12"/></svg>Copied</>
                ) : (
                  <><svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>Copy template</>
                )}
              </button>
              <a href={credRow.data.delivery.discordUrl} target="_blank" rel="noopener noreferrer" className="btn-discord rounded-xl px-4 py-2.5 text-xs">
                <DiscordIcon size={16} white className="shrink-0" />
                Open Discord Ticket
              </a>
            </div>
          </div>
        </div>
      ) : null}

      {statusRow.ok && statusRow.data.readyForDelivery && !hasBuyerSmmOrder(statusRow.data) ? (
        <div className="mt-5 rounded-2xl border overflow-hidden" style={{ borderColor: 'rgba(0, 191, 255,0.15)', background: 'rgba(10,8,16,0.8)' }}>
          <div className="border-b px-5 py-4" style={{ borderColor: 'rgba(0, 191, 255,0.1)', background: 'rgba(0, 191, 255,0.04)' }}>
            <h2 className="m-0 text-sm font-extrabold tracking-tight text-[#f0f6ff]">
              After delivery — vouch in{' '}
              <a href={PulseDigital_VOUCHES_CHANNEL_URL} target="_blank" rel="noopener noreferrer" className="text-[#a5d8ff] underline decoration-[#00bfff]/40 underline-offset-2 hover:text-[#93c5fd]">
                {PulseDigital_VOUCHES_CHANNEL}
              </a>
            </h2>
            <p className="m-0 mt-1 text-xs leading-relaxed text-[#5a6d8a]">
              Test your order when staff delivers. If it works, post this in Discord{' '}
              <a href={PulseDigital_VOUCHES_CHANNEL_URL} target="_blank" rel="noopener noreferrer" className="font-medium text-[#a5d8ff] underline decoration-[#00bfff]/40 underline-offset-2 hover:text-[#93c5fd]">
                {PulseDigital_VOUCHES_CHANNEL}
              </a>{' '}
              within {VOUCH_POST_DEADLINE_HOURS} hours to activate your warranty. If it failed on first use, reply in your ticket before vouching.
            </p>
          </div>
          <div className="p-5 sm:p-6">
            <pre className="max-h-48 overflow-auto whitespace-pre-wrap break-words rounded-xl border border-white/[0.06] p-4 font-mono text-xs leading-relaxed text-[#e8ecff]" style={{ background: 'rgba(0,0,0,0.2)' }}>
              {vouchTemplate}
            </pre>
            <div className="mt-3 flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => void copyText(vouchTemplate, 'vouch')}
                className="inline-flex items-center gap-1.5 rounded-xl border border-white/[0.06] bg-white/[0.02] px-4 py-2.5 text-xs font-semibold text-[#7a92b8] transition-colors hover:text-[#f0f6ff] hover:border-[rgba(0, 191, 255,0.15)]"
              >
                {copied === 'vouch' ? (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#34d399" strokeWidth="3">
                      <polyline points="20 6 9 17 4 12" />
                    </svg>
                    Copied
                  </>
                ) : (
                  <>
                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="9" y="9" width="13" height="13" rx="2" />
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                    </svg>
                    Copy vouch template
                  </>
                )}
              </button>
              <a
                href={PulseDigital_VOUCHES_CHANNEL_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-discord rounded-xl px-4 py-2.5 text-xs"
              >
                <DiscordIcon size={16} white className="shrink-0" />
                Open {PulseDigital_VOUCHES_CHANNEL}
              </a>
            </div>
          </div>
        </div>
      ) : null}

      <style dangerouslySetInnerHTML={{ __html: '@keyframes spin{to{transform:rotate(360deg)}}@keyframes ping{75%,100%{transform:scale(2);opacity:0}}' }} />
    </CustomerChrome>
  )
}
